import cloneDeep from 'lodash/cloneDeep'
import liveApi from 'live/lib/api'
import { reactWidgetWrapper } from 'live/widgets/decorators/react'
import widgetService from 'live/services/widgets'
import { LiveMultiValuesWidget } from './LiveMultiValuesWidget'
import { liveMultiValuesWidgetReducer } from './reducer'
import { LiveMultiValuesDashboardConfigForm, LiveMultiValuesEditorConfigForm } from './config/ConfigForm'
import { CustomSettingsByChannel, WIDGET_DEFAULT_CONFIG, WIDGET_POLICIES, WIDGET_TYPE } from './api'
import { loadViewerOverride } from './viewerOverride'
import './widget-icon.css'

/**
 * For the channel selection to work the query needs to use the identity lookup:
 *
 * def @@selected_mnemonics: (@@lookup.live_widget_multi_values_identity() ?? ()):const;
 *
 * @param originalRequest
 * @param _params
 * @param source
 */
// exported (only) so it's directly unit-testable - no behavior change
export const interceptor: WidgetRequestInterceptor<DashboardQueryParams, 'widget-editor' | 'dashboard' | string> = (
    originalRequest,
    _params,
    source
): LiveWidgetRequest<Record<string, unknown>, Record<string, unknown>> => {
    if (originalRequest.config?.widgetType === WIDGET_TYPE && originalRequest.config?.selectedChannels != null) {
        const request = cloneDeep(originalRequest)
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const config = request.userConfig ?? request.config

        const canonicalSelectedChannels: CustomSettingsByChannel[] = config?.selectedChannels?.length
            ? config.selectedChannels
            : []

        // a viewer's own browser-local override (see viewerOverride.ts) lives in localStorage, keyed by
        // widget id, and is invisible to the platform's own request config built above - it's never
        // written to the canonical jsonConfig. Without folding it in here too, a channel a viewer added
        // back locally (e.g. via the Removed dynamic channels panel's Reset, or Settings' own Save)
        // would show in their Selected list - LiveMultiValuesWidget.tsx renders from that same override - but
        // this identity lookup would still be built from canonical alone, so the query would never be
        // asked to compute a value for it: the box would appear with no data at all. `source` is what
        // LiveMultiValuesWidget.tsx itself keys the same distinction on (isWidgetEditMode) - only the
        // standalone widget editor (`widget-editor`) always works against canonical with no override.
        const selectedChannels =
            source === 'widget-editor'
                ? canonicalSelectedChannels
                : loadViewerOverride(request.widgetId)?.channels?.selectedChannels ?? canonicalSelectedChannels

        // lookupValues isn't guaranteed present on every request shape this interceptor sees -
        // initialize it rather than assuming the platform always populates it
        request.lookupValues = request.lookupValues ?? {}
        request.lookupValues.live_widget_multi_values_identity = selectedChannels.map(c => c.mnemonic) ?? []

        return request
    }

    return originalRequest
}

liveApi.Interceptor.dashboard.requests.register(interceptor)
liveApi.Interceptor.widget.requests.register(interceptor)

/**
 * Set up the Live Multi Values widget
 */
export function setupMultiValuesWidget(): void {
    widgetService.register({
        type: WIDGET_TYPE,
        name: 'Live Multi Values',
        description: 'Live Multi Values',
        impl: reactWidgetWrapper(LiveMultiValuesWidget, liveMultiValuesWidgetReducer),
        policies: WIDGET_POLICIES,
        config: WIDGET_DEFAULT_CONFIG,
        ui: {
            configForm: LiveMultiValuesEditorConfigForm
        }
    })
    widgetService.dashboardActions(WIDGET_TYPE, 'view', LiveMultiValuesDashboardConfigForm)
}
